import React, { useRef, useEffect, useState } from 'react';
import api from '../../api/axios';
import { ChevronDown, DownloadIcon } from 'lucide-react';
import QRDesigner from '../../pages/QREditor';
import Popup from '../popup/Popup';

const Layout2Form = ({ data, onChange, errors }) => {
  const { video, brand } = data;
  const [campaigns, setCampaigns] = useState([]);
  const [openQrCodePopup, setOpenQrCodePopup] = useState(false);
  const fieldRefs = useRef({});

  const handleChange = (section, field, value) => {
    let cleanedValue = (value ?? "").toString().replace(/%/g, "");

    if (field === 'url' || field === 'logoUrl' || field === 'imageUrl' || field === 'qrCodeUrl') {
      cleanedValue = value
    }
    onChange('layout2', section, field, cleanedValue);
  };

  useEffect(() => {
    if (!errors) return;

    const firstErrorKey = Object.keys(errors)[0];
    const el = fieldRefs.current[firstErrorKey];

    if (el) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      // el.focus(); // optional (nice UX)
    }
  }, [errors]);

  const getCampaigns = async () => {
    try {
      const response = await api.get('campaign/list');
      if (response && response.data && response.data.data) {
        setCampaigns(response.data.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getCampaigns();
  }, []);


  return (
    <div className="layout2-form">
      {/* Canvas Settings */}

      <div className="form-section">
        <div className="form-group">
          <label>Campaign Name</label>
          <div className='relative'>
            <select
              value={data.camp_name}
              ref={(el) => (fieldRefs.current["camp_name"] = el)}
              onChange={(e) => handleChange(null, 'camp_name', e.target.value)}
              className={`appearance-none ${errors?.camp_name ? "has-error" : ""} `}
            >
              <option value="">Select Campaign</option>
              {campaigns.map((camp) => (
                <option key={camp.camp_id} value={camp.camp_name}>
                  {camp.camp_name}
                </option>
              ))}
            </select>
            <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none" />
          </div>
          {errors?.camp_name && (
            <p className="text-red-500 text-sm mt-1">
              {errors.camp_name}
            </p>
          )}
        </div>

        <div className="form-group">
          <label>Brand Name</label>

          <input
            type="text"
            value={data.brand_name || ''}
            onChange={(e) => handleChange(null, 'brand_name', e.target.value)}
            className={errors?.brand_name ? "has-error" : ""}
            ref={(el) => (fieldRefs.current["brand_name"] = el)}
          />
          {errors?.brand_name && (
            <p className="text-red-500 text-sm mt-1">
              {errors.brand_name}
            </p>
          )}
        </div>

        <div className="form-group">
          <label>Domain</label>
          <input
            type="text"
            value={data.domain || ''}
            onChange={(e) => handleChange(null, 'domain', e.target.value)}
            className={errors?.domain ? "has-error" : ""}
            ref={(el) => (fieldRefs.current["domain"] = el)}
          />
          {errors?.domain && (
            <p className="text-red-500 text-sm mt-1">
              {errors.domain}
            </p>
          )}
        </div>

        <div className="form-group">
          <label>Creative Name</label>
          <input
            type="text"
            value={data.creative_name || ''}
            onChange={(e) => handleChange(null, 'creative_name', e.target.value)}
            className={errors?.creative_name ? "has-error" : ""}
            ref={(el) => (fieldRefs.current["creative_name"] = el)}
          />
          {errors?.creative_name && (
            <p className="text-red-500 text-sm mt-1">
              {errors.creative_name}
            </p>
          )}
        </div>
      </div>

      <div className="form-section">
        <h3>Canvas Settings</h3>

        <div className="form-group">
          <label>Background Type</label>
          <div className='relative'>
            <select
              value={data.canvas?.backgroundType || 'solid'}
              onChange={(e) => handleChange('canvas', 'backgroundType', e.target.value)}
              className='appearance-none'
            >
              <option value="solid">Solid</option>
              <option value="gradient">Gradient</option>
            </select>
            <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none" />
          </div>
        </div>
        {(data.canvas?.backgroundType || 'solid') === 'solid' && (
          <div className="form-group">
            <label>Background Color</label>
            <div className="color-input-group">
              <input
                type="color"
                value={data.canvas?.backgroundColor || '#e0e0e0'}
                onChange={(e) => handleChange('canvas', 'backgroundColor', e.target.value)}
              />
              <input
                type="text"
                value={data.canvas?.backgroundColor || '#e0e0e0'}
                onChange={(e) => handleChange('canvas', 'backgroundColor', e.target.value)}
                placeholder="#e0e0e0"
                className="color-text-input"
              />
            </div>
          </div>
        )}

        {data.canvas?.backgroundType === 'gradient' && (
          <>
            <div className="form-group">
              <label>Gradient Color 1</label>
              <div className="color-input-group">
                <input
                  type="color"
                  value={data.canvas?.gradientColor1 || data.canvas?.backgroundColor || '#e0e0e0'}
                  onChange={(e) => handleChange('canvas', 'gradientColor1', e.target.value)}
                />
                <input
                  type="text"
                  value={data.canvas?.gradientColor1 || data.canvas?.backgroundColor || '#e0e0e0'}
                  onChange={(e) => handleChange('canvas', 'gradientColor1', e.target.value)}
                  placeholder="#e0e0e0"
                  className="color-text-input"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Gradient Color 2</label>
              <div className="color-input-group">
                <input
                  type="color"
                  value={data.canvas?.gradientColor2 || '#ffffff'}
                  onChange={(e) => handleChange('canvas', 'gradientColor2', e.target.value)}
                />
                <input
                  type="text"
                  value={data.canvas?.gradientColor2 || '#ffffff'}
                  onChange={(e) => handleChange('canvas', 'gradientColor2', e.target.value)}
                  placeholder="#ffffff"
                  className="color-text-input"
                />
              </div>
            </div>
            <div className="form-group hidden">
              <label>Gradient Angle (deg)</label>
              <input
                type="number"
                min="0"
                max="360"
                value={data.canvas?.gradientAngle ?? 135}
                onChange={(e) => handleChange('canvas', 'gradientAngle', parseInt(e.target.value, 10) || 0)}
              />
            </div>
          </>
        )}
        {/* <div className="form-group">
          <label>Padding (px)</label>
          <input
            type="number"
            min="0"
            max="100"
            value={data.canvas?.padding || 20}
            onChange={(e) => handleChange('canvas', 'padding', parseInt(e.target.value) || 0)}
          />
        </div> */}
      </div>

      {/* Video Section */}
      <div className="form-section">
        <h3>Video</h3>
        <div className="form-group">
          <label>Video URL (1920 x 1080)</label>
          <input
            type="text"
            value={video.url}
            onChange={(e) => handleChange('video', 'url', e.target.value)}
            placeholder="Enter video URL"
          />
          <span className='text-xs text-blue-500'>Video Size: (1920 x 1080)</span>
        </div>
        {/* <div className="form-row">
          <div className="form-group">
            <label>Width (%)</label>
            <input
              type="number"
              min="10"
              max="100"
              value={video.width || 100}
              onChange={(e) => handleChange('video', 'width', parseInt(e.target.value) || 100)}
            />
          </div>
          <div className="form-group">
            <label>Height (%)</label>
            <input
              type="number"
              min="10"
              max="100"
              value={video.height || 100}
              onChange={(e) => handleChange('video', 'height', parseInt(e.target.value) || 100)}
            />
          </div>
        </div> */}
        <div className="form-group hidden">
          <label>Video Shape</label>
          <div className='relative'>
            <select
              value={video.shape || 'rounded'}
              onChange={(e) => handleChange('video', 'shape', e.target.value)}
              className='appearance-none'
            >
              <option value="none">None</option>
              <option value="square">Square</option>
              <option value="rounded">Rounded</option>
              <option value="rounded-lg">Rounded Large</option>
              <option value="circle">Circle</option>
            </select>
            <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none" />
          </div>
        </div>
        <div className="form-group hidden">
          <label>Shadow</label>
          <input
            type="text"
            value={video.shadow || '0 8px 30px rgba(0, 0, 0, 0.5)'}
            onChange={(e) => handleChange('video', 'shadow', e.target.value)}
            placeholder="0 8px 30px rgba(0, 0, 0, 0.5)"
          />
          <small className="form-hint">CSS box-shadow value</small>
        </div>
        <div className="form-group">
          <label>Title</label>
          <input
            type="text"
            value={video.subtitle1}
            onChange={(e) => handleChange('video', 'subtitle1', e.target.value)}
            placeholder="Suit at the dentist's office?"
          />
        </div>
        <div className="form-group">
          <label>Subtitle</label>
          <input
            type="text"
            value={video.subtitle2}
            onChange={(e) => handleChange('video', 'subtitle2', e.target.value)}
            placeholder="Wear what makes you feel good."
          />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Likes</label>
            <input
              type="text"
              value={video.likes}
              maxLength={5}
              onChange={(e) => handleChange('video', 'likes', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Comments</label>
            <input
              type="text"
              value={video.comments}
              maxLength={5}
              onChange={(e) => handleChange('video', 'comments', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Shares</label>
            <input
              type="text"
              maxLength={5}
              value={video.shares}
              onChange={(e) => handleChange('video', 'shares', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Brand Section */}
      <div className="form-section">
        <h3>Brand</h3>
        <div className="form-group">
          <label>Logo URL</label>
          <input
            type="text"
            value={brand.logoUrl}
            onChange={(e) => handleChange('brand', 'logoUrl', e.target.value)}
            placeholder="Enter logo URL"
          />
          <p className="text-blue-600 text-xs">
            Image must be at least 250 × 250 pixels.
          </p>
        </div>
        <div className="form-group">
          <label>QR Title</label>
          <input
            type="text"
            value={brand.website}
            onChange={(e) => handleChange('brand', 'website', e.target.value)}
            placeholder="hm.com"
          />
        </div>
        <div className="form-group">
          <label className='!flex justify-between cursor-pointer'
            onClick={() => setOpenQrCodePopup(true)}>QR Code URL <span className='hover:text-blue-800 mr-2'
            ><DownloadIcon className='h-4 w-4' /></span>
          </label>
          <input
            type="text"
            value={brand.qrCodeUrl}
            onChange={(e) => handleChange('brand', 'qrCodeUrl', e.target.value)}
            placeholder="Enter QR code URL"
          />
          {/* <p onClick={() => setOpenQrCodePopup(true)} className="text-blue-600 text-xs underline hover:text-blue-800 cursor-pointer">Generate QR Code</p> */}
        </div>
        <div className="form-group">
          <label>Shop Text</label>
          <input
            type="text"
            value={brand.shopText}
            onChange={(e) => handleChange('brand', 'shopText', e.target.value)}
            placeholder="Shop Now"
          />
        </div>
      </div>

      {/* Layout Background (inside Layout 2 frame) */}
      {/* <div className="form-section hidden">
        <h3>Layout Background</h3>
        <div className="form-group">
          <label>Background Type</label>
          <div className='relative'>
            <select
              value={background?.backgroundType || 'solid'}
              onChange={(e) => handleChange('background', 'backgroundType', e.target.value)}
              className='appearance-none'
            >
              <option value="solid">Solid</option>
              <option value="gradient">Gradient</option>
            </select>
            <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none" />
          </div>
        </div>
        <div className="form-group">
          <label>Color</label>
          <div className="color-input-group">
            <input
              type="color"
              value={background?.color || '#1a4d3a'}
              onChange={(e) => handleChange('background', 'color', e.target.value)}
            />
            <input
              type="text"
              value={background?.color || '#1a4d3a'}
              onChange={(e) => handleChange('background', 'color', e.target.value)}
              placeholder="#1a4d3a"
              className="color-text-input"
            />
          </div>
        </div>

        {background?.backgroundType === 'gradient' && (
          <>
            <div className="form-group">
              <label>Gradient Color 1</label>
              <div className="color-input-group">
                <input
                  type="color"
                  value={background?.gradientColor1 || background?.color || '#1a4d3a'}
                  onChange={(e) => handleChange('background', 'gradientColor1', e.target.value)}
                />
                <input
                  type="text"
                  value={background?.gradientColor1 || background?.color || '#1a4d3a'}
                  onChange={(e) => handleChange('background', 'gradientColor1', e.target.value)}
                  placeholder="#1a4d3a"
                  className="color-text-input"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Gradient Color 2</label>
              <div className="color-input-group">
                <input
                  type="color"
                  value={background?.gradientColor2 || '#0b2b20'}
                  onChange={(e) => handleChange('background', 'gradientColor2', e.target.value)}
                />
                <input
                  type="text"
                  value={background?.gradientColor2 || '#0b2b20'}
                  onChange={(e) => handleChange('background', 'gradientColor2', e.target.value)}
                  placeholder="#0b2b20"
                  className="color-text-input"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Gradient Angle (deg)</label>
              <input
                type="number"
                min="0"
                max="360"
                value={background?.gradientAngle ?? 135}
                onChange={(e) => handleChange('background', 'gradientAngle', parseInt(e.target.value, 10) || 0)}
              />
            </div>
          </>
        )}
      </div> */}
      <Popup isOpen={openQrCodePopup} onClose={() => setOpenQrCodePopup(false)}>
        <QRDesigner />
      </Popup>
    </div>
  );
};

export default Layout2Form;
